import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import type * as React from 'react';

import { cn } from '~/utils/cn.ts';

const Dialog = DialogPrimitive.Root;
const DialogTrigger = DialogPrimitive.Trigger;

function DialogPortal({
	children,
	className,
	...props
}: DialogPrimitive.DialogPortalProps & {
	className?: string;
}) {
	return (
		<DialogPrimitive.Portal {...props}>
			<div
				className={cn('fixed inset-0 z-[var(--z-index-dialog)] flex items-start justify-center', className)}
				data-slot="dialog-portal"
			>
				{children}
			</div>
		</DialogPrimitive.Portal>
	);
}

function DialogOverlay({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
	return (
		<DialogPrimitive.Overlay
			className={cn(
				'fixed inset-0 z-[var(--z-index-dialog)] bg-black/50',
				'data-[state=closed]:animate-out data-[state=open]:animate-in',
				'data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0',
				className,
			)}
			data-slot="dialog-overlay"
			{...props}
		/>
	);
}

function DialogClose({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Close>) {
	return (
		<DialogPrimitive.Close
			className={cn(
				'absolute top-4 right-4',
				'rounded-none opacity-70 transition-opacity',
				'hover:opacity-100',
				'focus:outline-none focus-visible:ring-1 focus-visible:ring-ring',
				'disabled:pointer-events-none',
				'data-[state=open]:bg-accent data-[state=open]:text-muted-foreground',
				className,
			)}
			data-slot="dialog-close"
			{...props}
		>
			<X className="size-4" />
			<span className="sr-only">Close</span>
		</DialogPrimitive.Close>
	);
}

function DialogContent({
	classNames,
	className,
	children,
	isCloseable = true,
	...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
	isCloseable?: boolean;
	classNames?: {
		content?: string;
		overlay?: string;
		portal?: string;
	};
}) {
	return (
		<DialogPortal className={classNames?.portal}>
			<DialogOverlay className={classNames?.overlay} />
			<DialogPrimitive.Content
				className={cn(
					// Lyra style: sharp corners
					'fixed z-[calc(var(--z-index-dialog)+1)] grid w-full gap-4 border bg-background p-6 shadow-lg',
					'rounded-none sm:max-w-lg',
					// Animation
					'data-[state=closed]:animate-out data-[state=open]:animate-in',
					'data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0',
					'data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-95',
					'data-[state=open]:slide-in-from-bottom-4',
					'data-[state=closed]:slide-out-to-bottom-4',
					// Position - centered horizontally, 10% from top
					'top-[10%] left-1/2 -translate-x-1/2',
					classNames?.content,
					className,
				)}
				data-slot="dialog-content"
				{...props}
			>
				{children}
				{isCloseable && <DialogClose />}
			</DialogPrimitive.Content>
		</DialogPortal>
	);
}

function DialogHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
	return (
		<div
			className={cn('flex flex-col gap-y-1.5 text-center sm:text-left', className)}
			data-slot="dialog-header"
			{...props}
		/>
	);
}

function DialogFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
	return (
		<div
			className={cn('flex flex-col-reverse sm:flex-row sm:justify-end sm:gap-x-2', className)}
			data-slot="dialog-footer"
			{...props}
		/>
	);
}

function DialogTitle({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Title>) {
	return (
		<DialogPrimitive.Title
			className={cn('font-semibold text-base leading-none tracking-tight', className)}
			data-slot="dialog-title"
			{...props}
		/>
	);
}

function DialogDescription({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Description>) {
	return (
		<DialogPrimitive.Description
			className={cn('text-muted-foreground text-xs', className)}
			data-slot="dialog-description"
			{...props}
		/>
	);
}

export {
	Dialog,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
};
